import { themeV2 } from '@features/executive-report/domain/theme-v2';
import { slideMasters } from '@features/executive-report/utils/slide-masters';
import {
  ImageProps,
  ImageWithMetadata,
  ShapeProps,
  Slide,
  TextProps,
} from '@features/powerpoint';
import { cardBackground } from './components/card-background';
import { centeredImage } from '../centered-image';

const DIMENSION_CARD_X_OFFSET = 3.03;
const DIMENSION_CARD_Y_OFFSET = 2.27;
const DIMENSION_CARDS_PER_ROW = 3;
const OVERALL_METRIC_Y_OFFSET = 0.85;

const getDimensionPosition = (
  index: number,
  xStart: number,
  yStart: number
): Pick<ShapeProps['options'], 'x' | 'y'> => ({
  x: xStart + (index % DIMENSION_CARDS_PER_ROW) * DIMENSION_CARD_X_OFFSET,
  y:
    yStart +
    Math.floor(index / DIMENSION_CARDS_PER_ROW) * DIMENSION_CARD_Y_OFFSET,
});

type SlideProps = {
  navbarText: TextProps;
  headerText: TextProps;
  overallMetrics: {
    label: string;
    value: string;
    lift?: string;
  }[];
  dimensionName: string;
  dimensionMetrics: {
    dimension: string;
    placeholder?: string;
    image: ImageWithMetadata | null;
    metrics: {
      label: string;
      value: string;
    }[];
  }[];
};

const overallMetricTexts = (
  overallMetrics: SlideProps['overallMetrics'][number],
  index: number
): TextProps[] => {
  return [
    {
      text: overallMetrics.label.toLocaleUpperCase(),
      options: {
        fontSize: 9,
        valign: 'middle',
        h: 0.25,
        w: 2.71,
        x: 0.65,
        y: 3.26 + index * OVERALL_METRIC_Y_OFFSET,
      },
    },
    {
      text: overallMetrics.value,
      options: {
        fontSize: 20,
        bold: true,
        valign: 'middle',
        h: 0.5,
        w: 2,
        x: 0.65,
        y: 3.44 + index * OVERALL_METRIC_Y_OFFSET,
      },
    },
    {
      text: overallMetrics.lift ?? '',
      options: {
        fontSize: 20,
        valign: 'middle',
        color: themeV2.colors.inmarketGreen,
        h: 0.5,
        w: 1.65,
        x: 2.03,
        y: 3.44 + index * OVERALL_METRIC_Y_OFFSET,
      },
    },
  ];
};

const dimensionMetricTexts = (
  dimensionMetric: SlideProps['dimensionMetrics'][number],
  placeholder: string,
  index: number
): TextProps[] => {
  return [
    {
      text: dimensionMetric.dimension,
      options: {
        fontSize: 11,
        h: 0.29,
        w: 2.27,
        ...getDimensionPosition(index, 4.08, 2.71),
      },
    },
    ...(dimensionMetric.image
      ? []
      : ([
          {
            text: (index + 1).toString(),
            options: {
              fontSize: 32,
              color: themeV2.colors.inmarketGreen,
              valign: 'middle',
              h: 0.64,
              w: 0.36,
              ...getDimensionPosition(index, 4.56, 3.23),
            },
          },
          {
            text: placeholder.toLocaleUpperCase(),
            options: {
              fontSize: 12,
              bold: true,
              h: 0.28,
              w: 1.21,
              ...getDimensionPosition(index, 4.92, 3.43),
            },
          },
        ] as const)),
    ...dimensionMetric.metrics.flatMap((metric, metricIndex): TextProps[] => [
      {
        text: metric.label.toLocaleUpperCase(),
        options: {
          fontSize: 8,
          valign: 'middle',
          h: 0.24,
          w: 1.46,
          ...getDimensionPosition(index, 4.01, 4.21 + metricIndex * 0.18),
        },
      },
      {
        text: metric.value,
        options: {
          fontSize: 12,
          valign: 'middle',
          bold: true,
          color: themeV2.colors.inmarketGreen,
          h: 0.3,
          w: 1.51,
          ...getDimensionPosition(index, 5.33, 4.18 + metricIndex * 0.18),
        },
      },
    ]),
  ];
};

export const dimensionSlide = (props: SlideProps): Slide => {
  return {
    slideMaster: slideMasters.light,
    color: themeV2.colors.metricBlue,
    images: props.dimensionMetrics.flatMap(
      (dimensionMetric, index): ImageProps[] => {
        if (dimensionMetric.image === null) return [];

        const position = getDimensionPosition(index, 4.09, 3.02);
        return [
          centeredImage({
            image: dimensionMetric.image,
            maxHeight: 1.09,
            maxWidth: 2.28,
            x: Number(position.x),
            y: Number(position.y),
          }),
        ];
      }
    ),
    shapes: [
      cardBackground({
        h: 5.14,
        w: 3.34,
        x: 0.42,
        y: 1.69,
      }),
      cardBackground({
        h: 0.72,
        w: 8.36,
        x: 4.09,
        y: 1.68,
      }),
      ...props.dimensionMetrics.flatMap(({ image }, index): ShapeProps[] => {
        return [
          ...(image
            ? []
            : [
                cardBackground({
                  h: 1.09,
                  w: 2.28,
                  ...getDimensionPosition(index, 4.09, 3.02),
                }),
              ]),
          {
            shapeName: 'line',
            options: {
              line: { color: themeV2.colors.inmarketGreen, width: 1 },
              h: 0,
              w: 2.27,
              ...getDimensionPosition(index, 4.08, 4.85),
            },
          },
        ];
      }),
    ],
    texts: [
      props.navbarText,
      props.headerText,
      {
        text: [
          {
            text: 'Overall ',
          },
          {
            text: 'Performance',
            options: { softBreakBefore: true },
          },
        ],
        options: {
          fontSize: 24,
          bold: true,
          h: 0.91,
          w: 3.1,
          x: 0.67,
          y: 2.05,
        },
      },
      ...props.overallMetrics.flatMap((overallMetric, index) =>
        overallMetricTexts(overallMetric, index)
      ),
      {
        text: [
          {
            text: `TOP ${props.dimensionMetrics.length} `,
          },
          {
            text: `${props.dimensionName.toLocaleUpperCase()}S`,
            options: { bold: true },
          },
        ],
        options: {
          fontSize: 16,
          align: 'center',
          h: 0.37,
          w: 8.36,
          x: 4.08,
          y: 1.88,
        },
      },
      ...props.dimensionMetrics.flatMap((dimensionMetric, index) =>
        dimensionMetricTexts(
          dimensionMetric,
          dimensionMetric.placeholder ?? props.dimensionName,
          index
        )
      ),
    ],
  };
};
